/**
 * @file Learners hooks — list (offset-paged, with debounced search) +
 *       per-row status mutation.
 * @module @labcraft/admin/ui/hooks/use-learners
 *
 * Hooks here:
 *
 *   - `useLearners({status, q, sort, dir, limit, offset})` — one page of
 *     learners for the numbered pager. Debounces `q` by 200ms, then embeds
 *     the **debounced** term (plus status, sort/dir, limit, offset) into
 *     the queryKey via `qkLearnersList(...)`. Any change is a fresh key →
 *     a fresh fetch; TanStack's in-flight cancellation means high-speed
 *     typing converges on the latest key alone. `placeholderData:
 *     keepPreviousData` keeps the current page on screen while the next
 *     one loads, so page turns don't flash empty. Returns `{ learners,
 *     total }` where `total` is the count across all pages (page count =
 *     ceil(total / limit)).
 *
 *     The debounced term MUST sit in the queryKey, not in the queryFn
 *     body — a stale `q` read from a ref would let the last-to-resolve
 *     fetch (not the most recent keystroke) win the cache.
 *
 *   - `useLearnerSetStatusMutation()` — POST status flip, invalidate
 *     every learners-scoped query on success so the next render re-fetches
 *     with the updated row.
 *
 *   - `useLearner(id)` — convenience lookup from the active list cache
 *     (the daemon has no `/api/learners/:id` GET); a cache miss returns
 *     `undefined`.
 *
 * Convention: hooks live in this directory and never call
 * `useQuery` / `useMutation` outside it.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import type {
  Learner,
  LearnersListResponse,
  LearnerStatus,
  UpdateLearnerProfileRequest,
} from '../../shared/routes.ts'
import { useDebounce } from './use_debounce.ts'
import {
  qkAllTags,
  qkLearnersList,
  qkLearnerTags,
  type LearnersListFilter,
} from './query_keys.ts'

/** Debounce window for the LearnersPanel search input. */
const LEARNERS_Q_DEBOUNCE_MS = 200

/**
 * Subscribe to one offset-paged page of learners matching the filter. `q`
 * is debounced inside the hook before it enters the queryKey, so a burst
 * of keystrokes resolves to a single fetch against the final key.
 *
 * The query is disabled while `limit === 0` (the panel's page size before
 * its container is measured), so no `limit=0` request is ever issued; an
 * absent `limit` lets the daemon apply its default page size.
 *
 * @param filter - Status + free-text search + sort + `limit`/`offset`.
 *   All optional.
 */
export function useLearners(filter: LearnersListFilter) {
  const client = useClient<Admin>()
  const debouncedQ = useDebounce(filter.q ?? '', LEARNERS_Q_DEBOUNCE_MS)
  const key: LearnersListFilter = {
    ...(filter.status ? { status: filter.status } : {}),
    ...(debouncedQ ? { q: debouncedQ } : {}),
    ...(filter.sort ? { sort: filter.sort, dir: filter.dir } : {}),
    ...(filter.tags?.length ? { tags: filter.tags } : {}),
    ...(filter.tagsNot?.length ? { tagsNot: filter.tagsNot } : {}),
    ...(filter.limit !== undefined ? { limit: filter.limit } : {}),
    ...(filter.offset ? { offset: filter.offset } : {}),
  }
  return useQuery<LearnersListResponse>({
    queryKey: qkLearnersList(key),
    queryFn: () => client.learners.list(key),
    enabled: filter.limit === undefined || filter.limit > 0,
    placeholderData: keepPreviousData,
  })
}

/**
 * Flip a learner's access state. On success, invalidates every
 * learners-scoped query so the panel re-fetches with the new row.
 */
export function useLearnerSetStatusMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: LearnerStatus }) =>
      client.learners.setStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'learners'] })
    },
  })
}

/**
 * Operator partial edit of a learner's profile (#1639). On success,
 * invalidates the learners prefix so the list + drawer re-fetch with the
 * updated `display_name` / `timezone` / `avatar_url`.
 */
export function useLearnerUpdateProfileMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string
      patch: UpdateLearnerProfileRequest
    }) => client.learners.updateProfile(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'learners'] })
    },
  })
}

/**
 * Look up one learner from the active list cache. Returns `undefined`
 * when no list query has the row in scope — callers render a
 * loading / not-found surface in that case.
 *
 * The admin daemon doesn't yet expose `GET /api/learners/:id`, so
 * this is a cache-only read; if/when the daemon adds the route the
 * hook can switch to its own `useQuery` against `qkLearner(id)`.
 */
export function useLearner(id: string): Learner | undefined {
  const queryClient = useQueryClient()
  const queries = queryClient.getQueriesData<LearnersListResponse>({
    queryKey: ['admin', 'learners', 'list'],
  })
  for (const [, data] of queries) {
    const hit = data?.learners.find(l => l.id === id)
    if (hit) return hit
  }
  return undefined
}

/**
 * Seed one learner's tags from the active list cache's `tagsByLearnerId`
 * sidecar, so the drawer paints its chips instantly on open before the
 * authoritative `listTags(id)` refresh lands. `undefined` when no cached
 * list has the row's sidecar in scope.
 *
 * @param id - Learner id.
 */
function useLearnerTagsSeed(id: string): string[] | undefined {
  const queryClient = useQueryClient()
  const queries = queryClient.getQueriesData<LearnersListResponse>({
    queryKey: ['admin', 'learners', 'list'],
  })
  for (const [, data] of queries) {
    const hit = data?.tagsByLearnerId?.[id]
    if (hit) return hit
  }
  return undefined
}

/**
 * Subscribe to one learner's tag set (#1639) — the authoritative
 * single-learner read the drawer shows. Seeds from the list cache's
 * `tagsByLearnerId` (instant chips) and always refetches on open + after a
 * tag mutation invalidates the learners prefix.
 *
 * @param id - Learner id. The query is disabled for an empty id.
 */
export function useLearnerTags(id: string) {
  const client = useClient<Admin>()
  const seed = useLearnerTagsSeed(id)
  return useQuery<string[]>({
    queryKey: qkLearnerTags(id),
    queryFn: () => client.learners.listTags(id),
    enabled: id.length > 0,
    ...(seed !== undefined ? { placeholderData: seed } : {}),
  })
}

/**
 * Subscribe to every distinct tag across learners (#1639) — the
 * autocomplete source for the drawer's add-tag control and the list/compose
 * tag filters.
 */
export function useAllTags() {
  const client = useClient<Admin>()
  return useQuery<string[]>({
    queryKey: qkAllTags,
    queryFn: () => client.learners.listAllTags(),
  })
}

/**
 * Attach a tag to a learner (#1639). On success, writes the returned set
 * into the per-learner tags cache for instant feedback, then invalidates the
 * learners prefix so the list's `tagsByLearnerId` sidecar + the distinct-tags
 * autocomplete re-fetch with the new tag.
 */
export function useAttachTag() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tag }: { id: string; tag: string }) =>
      client.learners.attachTag(id, tag),
    onSuccess: (tags, { id }) => {
      queryClient.setQueryData(qkLearnerTags(id), tags)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'learners'] })
    },
  })
}

/**
 * Detach a tag from a learner (#1639). Same cache handling as
 * {@link useAttachTag}: instant set write + a learners-prefix invalidation.
 */
export function useDetachTag() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tag }: { id: string; tag: string }) =>
      client.learners.detachTag(id, tag),
    onSuccess: (tags, { id }) => {
      queryClient.setQueryData(qkLearnerTags(id), tags)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'learners'] })
    },
  })
}
